import { formatCurrency } from '../number/format';

const typoMultiplier = 1000;
const minimumPlausibleSuggestedOfferRatio = 0.5;
const maximumPlausibleSuggestedOfferRatio = 1;
const lowOfferRatio = 0.8;

export interface OfferAcknowledgements {
    typoOffer: number | null;
    lowOffer: number | null;
    highOffer: number | null;
}

export type OfferConfirmationDecision =
    | { type: 'none' }
    | { type: 'typo'; offer: number; suggestedOffer: number }
    | { type: 'low'; offer: number }
    | { type: 'high'; offer: number };

export function evaluateOfferConfirmation(
    offer: number,
    askingPrice: number,
    acknowledgements: OfferAcknowledgements,
): OfferConfirmationDecision {
    if ( !Number.isFinite( offer ) || offer <= 0 || !Number.isFinite( askingPrice ) || askingPrice <= 0 ) {
        return { type: 'none' };
    }

    if ( acknowledgements.typoOffer !== offer ) {
        const suggestedOffer = offer * typoMultiplier;
        const suggestedOfferRatio = suggestedOffer / askingPrice;

        if (
            suggestedOfferRatio >= minimumPlausibleSuggestedOfferRatio &&
            suggestedOfferRatio <= maximumPlausibleSuggestedOfferRatio
        ) {
            return {
                type: 'typo',
                offer,
                suggestedOffer,
            };
        }
    }

    if ( acknowledgements.lowOffer !== offer && offer < askingPrice * lowOfferRatio ) {
        return {
            type: 'low',
            offer,
        };
    }

    if ( acknowledgements.highOffer !== offer && offer > askingPrice ) {
        return {
            type: 'high',
            offer,
        };
    }

    return { type: 'none' };
}

export class OfferConfirmationState {
    private _acknowledgements: OfferAcknowledgements = {
        typoOffer: null,
        lowOffer: null,
        highOffer: null,
    };

    public values(): OfferAcknowledgements {
        return {
            typoOffer: this._acknowledgements.typoOffer,
            lowOffer: this._acknowledgements.lowOffer,
            highOffer: this._acknowledgements.highOffer,
        };
    }

    public acknowledgeTypo( offer: number ): void {
        this._acknowledgements.typoOffer = offer;
    }

    public acknowledgeLowOffer( offer: number ): void {
        this._acknowledgements.lowOffer = offer;
    }

    public acknowledgeHighOffer( offer: number ): void {
        this._acknowledgements.highOffer = offer;
    }

    public reset(): void {
        this._acknowledgements = {
            typoOffer: null,
            lowOffer: null,
            highOffer: null,
        };
    }
}

type ContinueSubmission = () => void;

export class PropertyOfferConfirmation {
    private readonly _offerCheckbox: HTMLInputElement | null;
    private readonly _offerInput: HTMLInputElement | null;
    private readonly _state = new OfferConfirmationState();
    private _confirmationElement: HTMLElement | null = null;

    public constructor( form: HTMLFormElement ) {
        this._offerCheckbox = form.querySelector<HTMLInputElement>( 'input[data-ts-cta-action="make-offer"]' );
        this._offerInput = form.querySelector<HTMLInputElement>( 'input[name="offer-quantity"]' );

        if ( this._offerInput ) {
            this._offerInput.addEventListener( 'input', () => this._reset() );
        }

        if ( this._offerCheckbox ) {
            this._offerCheckbox.addEventListener( 'change', () => this._reset() );
        }
    }

    public confirmBeforeSubmission( continueSubmission: ContinueSubmission ): boolean {
        const offerCheckbox = this._offerCheckbox;
        const offerInput = this._offerInput;

        if ( !offerCheckbox || !offerCheckbox.checked || !offerInput ) {
            this._removeConfirmation();
            return true;
        }

        const offer = Number( offerInput.value );
        const askingPrice = Number( offerInput.dataset.offerAskingPrice || 0 );
        const decision = evaluateOfferConfirmation( offer, askingPrice, this._state.values() );

        if ( decision.type === 'none' ) {
            this._removeConfirmation();
            return true;
        }

        if ( decision.type === 'typo' ) {
            this._showConfirmation(
                this._messageWithPrice( decision.suggestedOffer ),
                () => {
                    offerInput.value = decision.suggestedOffer.toString();
                    this._state.acknowledgeTypo( decision.suggestedOffer );
                    this._removeConfirmation();
                    continueSubmission();
                },
                () => {
                    this._state.acknowledgeTypo( decision.offer );
                    this._removeConfirmation();
                    continueSubmission();
                },
            );
            return false;
        }

        const isLowOffer = decision.type === 'low';
        let message = offerInput.dataset.offerHighMessage || '';

        if ( isLowOffer ) {
            message = offerInput.dataset.offerLowMessage || '';
        }

        this._showConfirmation(
            message,
            () => {
                if ( isLowOffer ) {
                    this._state.acknowledgeLowOffer( decision.offer );
                } else {
                    this._state.acknowledgeHighOffer( decision.offer );
                }
                this._removeConfirmation();
                continueSubmission();
            },
            () => {
                this._removeConfirmation();
                offerInput.focus();
                offerInput.select();
            },
        );
        return false;
    }

    private _messageWithPrice( suggestedOffer: number ): string {
        if ( !this._offerInput ) {
            return '';
        }

        const formattedPrice = formatCurrency( suggestedOffer, window.currency, window.currencySymbol );
        return ( this._offerInput.dataset.offerTypoMessage || '' ).replace( '{price}', formattedPrice );
    }

    private _showConfirmation( message: string, onYes: () => void, onNo: () => void ): void {
        this._removeConfirmation();

        const offerInput = this._offerInput;

        if ( !offerInput ) {
            return;
        }

        const confirmationElement = document.createElement( 'div' );
        confirmationElement.className = 'didyoumean offer-confirmation error-message';
        confirmationElement.dataset.offerConfirmation = 'true';
        confirmationElement.setAttribute( 'role', 'alert' );

        const messageElement = document.createElement( 'span' );
        messageElement.className = 'offer-confirmation__message';
        messageElement.textContent = message;
        confirmationElement.appendChild( messageElement );

        const actionsElement = document.createElement( 'span' );
        actionsElement.className = 'offer-confirmation__actions';

        const yesButton = this._createActionButton( 'yes', offerInput.dataset.offerYesLabel || '', onYes );
        const noButton = this._createActionButton( 'no', offerInput.dataset.offerNoLabel || '', onNo );
        actionsElement.append( yesButton, noButton );
        confirmationElement.appendChild( actionsElement );

        const inputGroup = offerInput.closest( '.input-group' );
        ( inputGroup || offerInput ).insertAdjacentElement( 'afterend', confirmationElement );
        this._confirmationElement = confirmationElement;
        yesButton.focus();
    }

    private _createActionButton( className: string, label: string, onClick: () => void ): HTMLButtonElement {
        const button = document.createElement( 'button' );
        button.type = 'button';
        button.className = className;
        button.textContent = label;
        button.addEventListener( 'click', onClick );
        return button;
    }

    private _reset(): void {
        this._state.reset();
        this._removeConfirmation();
    }

    private _removeConfirmation(): void {
        if ( this._confirmationElement ) {
            this._confirmationElement.remove();
        }
        this._confirmationElement = null;
    }
}
